// ═══════════════════════════════════════════════════════════════
// SHELTER — Public Pre-Registration (no auth required)
//
// Allows individuals to pre-register before arriving at a shelter.
// The link shared by the EOC carries ?incident=<id> so records
// are scoped to the active incident and visible to intake staff.
//
// Flow:
//   1. EOC copies shareable link from Shelter Dashboard
//   2. Public navigates to /shelter/pre-register?incident=<id>
//   3. Form captures primary registrant + optional family members
//   4. On submit: creates survivor records (status=pre-registered)
//      + a shelterFamilies record linking all members together
//   5. Success screen shows registration code for quick check-in
// ═══════════════════════════════════════════════════════════════

import { useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { IconShieldCheck, IconPlus, IconTrash, IconUserPlus, IconAlertTriangle } from '@tabler/icons-react';
import { createRecord } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';

const WORKER = import.meta.env.DEV
  ? (import.meta.env.VITE_WORKER_BASE || 'https://ogemc-worker.ogemc.workers.dev')
  : '/api/worker';

const FSv  = FIELDS.survivors;
const FFam = FIELDS.shelterFamilies;

function formatPhone(v: string) {
  const d = v.replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0,3)}) ${d.slice(3)}`;
  return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
}

interface FamilyMember {
  firstName: string; lastName: string; dateOfBirth: string; gender: string;
  specialNeeds: string; isMinor: boolean;
}
const EMPTY_MEMBER: FamilyMember = {
  firstName: '', lastName: '', dateOfBirth: '', gender: '', specialNeeds: '', isMinor: false,
};

// ── Address suggestion type ───────────────────────────────────
interface AddrSuggestion {
  label: string;
  street: string; city: string; state: string; zip: string;
}

export default function PublicPreRegister() {
  const [params] = useSearchParams();
  const incidentId = params.get('incident') ?? '';

  // Primary registrant
  const [firstName, setFirstName]           = useState('');
  const [lastName,  setLastName]            = useState('');
  const [phone,     setPhone]               = useState('');
  const [email,     setEmail]               = useState('');
  const [homeAddress, setHomeAddress]       = useState('');
  const [city,      setCity]                = useState('');
  const [state,     setState]               = useState('');
  const [zip,       setZip]                 = useState('');
  const [ecName,    setEcName]              = useState('');
  const [ecPhone,   setEcPhone]             = useState('');
  const [specialNeeds, setSpecialNeeds]     = useState('');
  const [dietaryNeeds, setDietaryNeeds]     = useState('');
  const [medications,  setMedications]      = useState('');

  // Family
  const [familyMembers, setFamilyMembers]   = useState<FamilyMember[]>([]);

  // Address autocomplete
  const [addrSuggestions, setAddrSuggestions] = useState<AddrSuggestion[]>([]);
  const [addrOpen, setAddrOpen]               = useState(false);
  const addrRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchAddr = (q: string) => {
    setHomeAddress(q);
    if (addrRef.current) clearTimeout(addrRef.current);
    if (q.length < 3) { setAddrSuggestions([]); setAddrOpen(false); return; }
    addrRef.current = setTimeout(async () => {
      try {
        const r = await fetch(
          `${WORKER}/api/here/autosuggest?q=${encodeURIComponent(q)}&at=33.749,-84.388&limit=5`,
          { headers: { Origin: 'https://apps.opengov.com' } }
        );
        if (!r.ok) return;
        const d = await r.json();
        const suggestions: AddrSuggestion[] = (d.items ?? [])
          .filter((i: any) => i.address?.label)
          .map((i: any) => ({
            label:  i.address.label,
            street: i.address.street   ?? '',
            city:   i.address.city     ?? '',
            state:  i.address.stateCode ?? i.address.state ?? '',
            zip:    i.address.postalCode ?? '',
          }));
        setAddrSuggestions(suggestions);
        setAddrOpen(suggestions.length > 0);
      } catch { /* ignore */ }
    }, 300);
  };

  const selectAddr = (s: AddrSuggestion) => {
    setHomeAddress(s.street || s.label);
    if (s.city)  setCity(s.city);
    if (s.state) setState(s.state);
    if (s.zip)   setZip(s.zip);
    setAddrSuggestions([]);
    setAddrOpen(false);
  };

  const addMember    = () => setFamilyMembers(p => [...p, { ...EMPTY_MEMBER }]);
  const removeMember = (i: number) => setFamilyMembers(p => p.filter((_, j) => j !== i));
  const setMember    = (i: number, k: keyof FamilyMember, v: string | boolean) =>
    setFamilyMembers(p => p.map((m, j) => j === i ? { ...m, [k]: v } : m));

  const [submitting, setSubmitting] = useState(false);
  const [regCode,    setRegCode]    = useState('');
  const [submitted,  setSubmitted]  = useState(false);
  const [error,      setError]      = useState('');

  const handleSubmit = async () => {
    if (!firstName.trim() || !lastName.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const code = `PRE-${Date.now().toString(36).toUpperCase()}`;
      const now  = new Date().toISOString();

      // Build primary survivor payload
      const primary: Record<string, unknown> = {
        [FSv.firstName]:    firstName.trim(),
        [FSv.lastName]:     lastName.trim(),
        [FSv.phone]:        phone,
        [FSv.email]:        email,
        [FSv.homeAddress]:  homeAddress,
        [FSv.city]:         city,
        [FSv.state]:        state,
        [FSv.zip]:          zip,
        [FSv.emergencyContactName]:  ecName,
        [FSv.emergencyContactPhone]: ecPhone,
        [FSv.specialNeeds]: specialNeeds,
        [FSv.dietaryNeeds]: dietaryNeeds,
        [FSv.medications]:  medications,
        [FSv.status]:       'pre-registered',
        [FSv.qrCode]:       code,
        [FSv.intakeTimestamp]: now,
      };
      if (incidentId) primary[FSv.incidentId] = incidentId;

      const primaryRes = await createRecord<{ record_id: number }>(TABLES.survivors, primary);
      const primaryId = String(primaryRes.record_id);
      const memberIds: string[] = [primaryId];

      // Create family members
      for (const m of familyMembers) {
        if (!m.firstName.trim()) continue;
        const mem: Record<string, unknown> = {
          [FSv.firstName]:    m.firstName.trim(),
          [FSv.lastName]:     m.lastName.trim() || lastName.trim(),
          [FSv.dateOfBirth]:  m.dateOfBirth,
          [FSv.gender]:       m.gender,
          [FSv.specialNeeds]: m.specialNeeds,
          [FSv.isMinor]:      m.isMinor ? 'true' : 'false',
          [FSv.status]:       'pre-registered',
          [FSv.qrCode]:       `${code}-${memberIds.length}`,
          [FSv.intakeTimestamp]: now,
        };
        if (incidentId) mem[FSv.incidentId] = incidentId;
        const memRes = await createRecord<{ record_id: number }>(TABLES.survivors, mem);
        memberIds.push(String(memRes.record_id));
      }

      // Create a shelterFamilies record linking everyone
      if (memberIds.length > 0) {
        const famPayload: Record<string, unknown> = {
          [FFam.familyName]:      `${lastName.trim()} Family`,
          [FFam.memberCount]:     memberIds.length,
          [FFam.headOfHousehold]: primaryId,
          [FFam.notes]:           `Pre-registered: ${code}`,
        };
        if (incidentId) famPayload[FFam.incidentId] = incidentId;
        try {
          await createRecord(TABLES.shelterFamilies, famPayload);
        } catch {
          // shelterFamilies may not have public write access — non-fatal
          // staff can link the family at check-in time
        }
      }

      setRegCode(code);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success screen ────────────────────────────────────────────

  if (submitted) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={{ textAlign: 'center' }}>
            <div style={styles.successIcon}><IconShieldCheck size={36} color="#07963F" /></div>
            <h2 style={{ margin: '16px 0 8px', color: '#037730', fontSize: 24 }}>Pre-Registration Complete</h2>
            <p style={{ color: '#7B7D7F', marginBottom: 24, fontSize: 14 }}>
              {firstName} {lastName}
              {familyMembers.filter(m => m.firstName.trim()).length > 0
                ? ` and ${familyMembers.filter(m => m.firstName.trim()).length} family member(s) have`
                : ' has'} been pre-registered.
            </p>

            <div style={styles.codeBox}>
              <div style={{ fontSize: 12, color: '#7B7D7F', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Your Registration Code</div>
              <div style={{ fontFamily: 'monospace', fontSize: 32, fontWeight: 800, color: '#4B3FFF', letterSpacing: '0.05em' }}>{regCode}</div>
              <div style={{ fontSize: 12, color: '#7B7D7F', marginTop: 8 }}>
                Show this code when you arrive at the shelter for quick check-in.
              </div>
            </div>

            <button style={styles.btnLight} onClick={() => {
              setSubmitted(false);
              setFirstName(''); setLastName(''); setPhone(''); setEmail('');
              setHomeAddress(''); setCity(''); setState(''); setZip('');
              setEcName(''); setEcPhone('');
              setSpecialNeeds(''); setDietaryNeeds(''); setMedications('');
              setFamilyMembers([]);
            }}>
              Register Another Person
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Registration form ─────────────────────────────────────────

  return (
    <div style={styles.page}>
      <div style={{ ...styles.card, maxWidth: 720 }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={styles.successIcon}><IconUserPlus size={24} color="#4B3FFF" /></div>
          <h2 style={{ margin: '8px 0 4px', fontSize: 24, color: '#1B1C1C' }}>Shelter Pre-Registration</h2>
          <p style={{ color: '#7B7D7F', fontSize: 14, margin: 0 }}>
            Pre-register yourself and your family. This speeds up check-in when you arrive.
          </p>
          {!incidentId && (
            <div style={styles.warnBanner}>
              <IconAlertTriangle size={14} style={{ marginRight: 8, flexShrink: 0 }} />
              Use the link provided by your emergency management office to ensure your registration is linked to the correct event.
            </div>
          )}
        </div>

        <hr style={styles.divider} />
        <div style={styles.sectionLabel}>Primary Registrant</div>

        <div style={styles.grid2}>
          <Field label="First Name *">
            <input style={styles.input} value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First name" />
          </Field>
          <Field label="Last Name *">
            <input style={styles.input} value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last name" />
          </Field>
          <Field label="Phone">
            <input style={styles.input} value={phone} onChange={e => setPhone(formatPhone(e.target.value))} placeholder="(555) 123-4567" />
          </Field>
          <Field label="Email">
            <input style={styles.input} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@example.com" />
          </Field>
        </div>

        {/* Address with autocomplete */}
        <Field label="Home Address" style={{ position: 'relative', marginBottom: 16 }}>
          <input
            style={styles.input}
            value={homeAddress}
            onChange={e => fetchAddr(e.target.value)}
            onBlur={() => setTimeout(() => setAddrOpen(false), 150)}
            placeholder="Start typing your address…"
          />
          {addrOpen && addrSuggestions.length > 0 && (
            <div style={styles.addrDropdown}>
              {addrSuggestions.map((s, i) => (
                <button key={i} style={styles.addrOption} onMouseDown={() => selectAddr(s)}>
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </Field>

        <div style={styles.grid3}>
          <Field label="City">
            <input style={styles.input} value={city} onChange={e => setCity(e.target.value)} placeholder="City" />
          </Field>
          <Field label="State">
            <input style={styles.input} value={state} onChange={e => setState(e.target.value)} placeholder="FL" maxLength={2} />
          </Field>
          <Field label="ZIP">
            <input style={styles.input} value={zip} onChange={e => setZip(e.target.value)} placeholder="32801" />
          </Field>
        </div>

        <div style={styles.grid2}>
          <Field label="Emergency Contact Name">
            <input style={styles.input} value={ecName} onChange={e => setEcName(e.target.value)} placeholder="Contact name" />
          </Field>
          <Field label="Emergency Contact Phone">
            <input style={styles.input} value={ecPhone} onChange={e => setEcPhone(formatPhone(e.target.value))} placeholder="(555) 123-4567" />
          </Field>
        </div>

        <Field label="Special Needs / Medical">
          <textarea style={{ ...styles.input, minHeight: 60, resize: 'vertical' }}
            value={specialNeeds} onChange={e => setSpecialNeeds(e.target.value)}
            placeholder="Wheelchair, oxygen, insulin, etc." />
        </Field>
        <Field label="Dietary Restrictions">
          <textarea style={{ ...styles.input, minHeight: 44, resize: 'vertical' }}
            value={dietaryNeeds} onChange={e => setDietaryNeeds(e.target.value)}
            placeholder="Gluten-free, halal, nut allergy, etc." />
        </Field>
        <Field label="Medications">
          <textarea style={{ ...styles.input, minHeight: 44, resize: 'vertical' }}
            value={medications} onChange={e => setMedications(e.target.value)}
            placeholder="List current medications" />
        </Field>

        {/* Family Members */}
        <hr style={styles.divider} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={styles.sectionLabel}>Family Members ({familyMembers.length})</div>
          <button style={styles.btnLight} onClick={addMember}>
            <IconPlus size={14} style={{ marginRight: 4 }} /> Add Member
          </button>
        </div>

        {familyMembers.map((m, idx) => (
          <div key={idx} style={styles.memberCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: '#4B3FFF' }}>Family Member {idx + 1}</span>
              <button style={styles.removeBtn} onClick={() => removeMember(idx)}>
                <IconTrash size={13} style={{ marginRight: 3 }} /> Remove
              </button>
            </div>
            <div style={styles.grid2}>
              <Field label="First Name">
                <input style={styles.input} value={m.firstName} onChange={e => setMember(idx, 'firstName', e.target.value)} />
              </Field>
              <Field label="Last Name">
                <input style={styles.input} value={m.lastName} onChange={e => setMember(idx, 'lastName', e.target.value)} placeholder={lastName || 'Same as primary'} />
              </Field>
              <Field label="Date of Birth">
                <input style={styles.input} type="date" value={m.dateOfBirth} onChange={e => setMember(idx, 'dateOfBirth', e.target.value)} />
              </Field>
              <Field label="Gender">
                <select style={styles.input} value={m.gender} onChange={e => setMember(idx, 'gender', e.target.value)}>
                  <option value="">— Select —</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Non-Binary">Non-Binary</option>
                  <option value="Other">Other</option>
                </select>
              </Field>
            </div>
            <Field label="Special Needs / Medications">
              <input style={styles.input} value={m.specialNeeds} onChange={e => setMember(idx, 'specialNeeds', e.target.value)} placeholder="Optional" />
            </Field>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#475569', marginTop: 8 }}>
              <input type="checkbox" checked={m.isMinor} onChange={e => setMember(idx, 'isMinor', e.target.checked)} />
              Under 18 (minor)
            </label>
          </div>
        ))}

        {error && (
          <div style={styles.errorBanner}>
            <IconAlertTriangle size={14} style={{ marginRight: 8, flexShrink: 0 }} />
            {error}
          </div>
        )}

        <button
          style={{ ...styles.btnPrimary, opacity: (!firstName.trim() || !lastName.trim() || submitting) ? 0.6 : 1 }}
          onClick={handleSubmit}
          disabled={!firstName.trim() || !lastName.trim() || submitting}
        >
          {submitting ? 'Submitting…' : 'Submit Pre-Registration'}
        </button>

        <p style={{ fontSize: 12, color: '#939598', textAlign: 'center', marginTop: 16 }}>
          OpenGov Emergency Management Center
        </p>
      </div>
    </div>
  );
}

// ── Small helpers ─────────────────────────────────────────────

function Field({ label, children, style }: { label: string; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ marginBottom: 16, ...style }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

// ── Inline styles ─────────────────────────────────────────────

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f0f4f8 0%, #d9e2ec 100%)',
    padding: 24,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
  } as React.CSSProperties,

  card: {
    background: 'white',
    borderRadius: 14,
    padding: '28px 32px',
    width: '100%',
    maxWidth: 520,
    
  } as React.CSSProperties,

  successIcon: {
    width: 64, height: 64, borderRadius: '50%', background: '#f0fdf4',
    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 4px',
  } as React.CSSProperties,

  codeBox: {
    background: '#F8F8F8', border: '2px solid #4B3FFF', borderRadius: 4,
    padding: '16px 24px', margin: '16px 0',
  } as React.CSSProperties,

  divider: { border: 'none', borderTop: '1px solid #f1f5f9', margin: '16px 0 16px' } as React.CSSProperties,

  sectionLabel: {
    fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
    color: '#939598', marginBottom: 16,
  } as React.CSSProperties,

  grid2: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px',
  } as React.CSSProperties,

  grid3: {
    display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0 16px',
  } as React.CSSProperties,

  input: {
    width: '100%', padding: '8px 8px', border: '1.5px solid #e2e8f0', borderRadius: 7,
    fontSize: 14, fontFamily: 'inherit', color: '#1B1C1C', background: 'white',
    boxSizing: 'border-box' as const,
  } as React.CSSProperties,

  addrDropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
    background: 'white', border: '1px solid #e2e8f0', borderRadius: 7,
    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)', marginTop: 2, overflow: 'hidden',
  } as React.CSSProperties,

  addrOption: {
    display: 'block', width: '100%', textAlign: 'left' as const,
    padding: '9px 16px', border: 'none', background: 'none', cursor: 'pointer',
    fontSize: 14, fontFamily: 'inherit', color: '#1B1C1C',
    borderBottom: '1px solid #f1f5f9',
  } as React.CSSProperties,

  memberCard: {
    background: '#F8F8F8', border: '1.5px solid #e2e8f0', borderRadius: 4,
    padding: '16px 16px', marginBottom: 16,
  } as React.CSSProperties,

  warnBanner: {
    display: 'flex', alignItems: 'center', marginTop: 16,
    background: '#fffbeb', border: '1px solid #fde68a',
    borderRadius: 4, padding: '8px 16px', fontSize: 12, color: '#92400e',
  } as React.CSSProperties,

  errorBanner: {
    display: 'flex', alignItems: 'center',
    background: '#fef2f2', border: '1px solid #fecaca',
    borderRadius: 4, padding: '8px 16px', fontSize: 14, color: '#B12525', marginBottom: 16,
  } as React.CSSProperties,

  btnPrimary: {
    width: '100%', padding: '16px 0', background: '#4B3FFF', color: 'white',
    border: 'none', borderRadius: 4, fontSize: 16, fontWeight: 700,
    cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center',
  } as React.CSSProperties,

  btnLight: {
    padding: '7px 16px', background: '#f1f5f9', color: '#4B3FFF',
    border: '1.5px solid #4B3FFF', borderRadius: 7, fontSize: 14, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center',
  } as React.CSSProperties,

  removeBtn: {
    display: 'inline-flex', alignItems: 'center', padding: '4px 8px',
    background: 'none', border: 'none', color: '#B12525', cursor: 'pointer',
    fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
  } as React.CSSProperties,
};
